import { useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";

import DatePicker from "../../components/DatePicker/DatePicker";
import TimePicker from "../../components/TimePicker/TimePicker";
import { useTournamentCreation } from "../../context/TournamentCreationContext";
import { DASHBOARD_STACK } from "../../utils/screenNames";
import uploadIcon from "../../assets/images/icons/upload-icon.svg";

const CompetitionsRefactored = () => {
  const navigate = useNavigate();
  const { formData, updateField, updateFields } = useTournamentCreation();

  // Local UI state
  const [isDragOver, setIsDragOver] = useState(false);

  // Derive from context
  const { name: tournamentName, startDate, endDate, dayStartTime, dayEndTime, logoFile: selectedFile, logoPreview: preview } = formData;

  // --- Handlers ---

  const handleFileChange = (file) => {
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/svg+xml", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload PNG, JPG, SVG or GIF.");
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error("File size too large. Maximum size is 5MB.");
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      updateFields({ logoFile: file, logoPreview: img.src });
    };
    img.onerror = () => {
      toast.error("Invalid image file.");
    };
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileChange(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleStartDateChange = (date) => {
    updateField("startDate", date);
    // Auto-set end date if empty or before start date
    if (!endDate || moment(date).isAfter(moment(endDate))) {
      updateField("endDate", date);
    }
  };

  const handleNextClick = () => {
    // Validations only — no API calls
    if (!tournamentName.trim()) {
      toast.error("Please enter a competition name");
      return;
    }
    if (!startDate) {
      toast.error("Please select a start date");
      return;
    }
    if (!endDate) {
      toast.error("Please select an end date");
      return;
    }

    navigate(DASHBOARD_STACK.COMPETITIONS_CONFIG);
  };

  return (
    <div className="h-full bg-[#000F1F] text-[#FEFCEB] flex flex-1 flex-col items-center overflow-hidden">
      {/* Container */}
      <div className="w-full max-w-2xl flex-1 flex flex-col items-center py-6">
        {/* Header */}
        <div className="flex flex-col gap-[16px] items-center justify-center shrink-0">
          <h1 className="text-[24px] sm:text-[32px] md:text-[40px] font-medium text-[#FEFCEB] text-center tracking-[1px]">Create new competition</h1>
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-3 sm:gap-[28px]">
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#00E7FF] text-[#000F1F] flex items-center justify-center font-semibold text-[14px]">1</div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#FEFCEB]">Competition details</span>
            </div>
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#192634] text-[#7F8585] flex items-center justify-center font-semibold text-[14px]">2</div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#7F8585]">Configuration</span>
            </div>
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#192634] text-[#7F8585] flex items-center justify-center font-semibold text-[14px]">3</div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#7F8585]">Venue details</span>
            </div>
          </div>
        </div>

        {/* Form Container */}
        <div className="w-full max-w-[480px] flex-1 my-5 mx-3 sm:mx-0 border border-app-border rounded-2xl p-4 sm:p-6 overflow-y-auto flex flex-col gap-[16px]">

          {/* Competition Name */}
          <div className="flex flex-col gap-2">
            <label className="block text-[14px] font-normal text-clubbie-white ">Competition name</label>
            <input
              type="text"
              placeholder="Competition name"
              value={tournamentName}
              onChange={(e) => updateField("name", e.target.value)}
              className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors [&::-webkit-calendar-picker-indicator]:invert"
            />
          </div>

          {/* Dates Row */}
          <div className="flex flex-col sm:flex-row gap-[16px]">
            <div className="flex-1 flex flex-col gap-2">
              <label className="block text-[14px] font-normal text-clubbie-white ">Start date</label>
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                disablePastDates={false}
              />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <label className="block text-[14px] font-normal text-clubbie-white ">End date</label>
              <DatePicker
                value={endDate}
                onChange={(date) => updateField("endDate", date)}
                minDate={startDate}
                disablePastDates={false}
              />
            </div>
          </div>

          {/* Day Start / End Time — hidden, defaults always sent */}
          <div className="hidden">
            <div className="flex gap-[16px]">
              <div className="flex-1 flex flex-col gap-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">Day start time</label>
                <TimePicker
                  value={dayStartTime || "09:00"}
                  onChange={(val) => updateField("dayStartTime", val)}
                />
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">Day end time</label>
                <TimePicker
                  value={dayEndTime || "21:00"}
                  onChange={(val) => updateField("dayEndTime", val)}
                />
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div className="flex flex-col gap-2">
            <label className="block text-[14px] font-normal text-clubbie-white ">Competition logo</label>
            <div
              className={`bg-[#000F1F] border border-[#192634] border-dashed rounded-[24px] p-[30px]  flex flex-col items-center justify-center cursor-pointer transition-colors ${
                isDragOver ? "border-[#00E7FF]" : ""
              }`}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => document.getElementById("logo-upload").click()}
            >
              {preview ? (
                <div className="relative w-full h-full flex items-center justify-center">
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-h-full max-w-full object-contain rounded-md"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      updateFields({ logoFile: null, logoPreview: null });
                    }}
                    className="absolute top-[-10px] right-[-10px] bg-red-500 rounded-full p-1 hover:bg-red-600 text-white transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-[8px] items-center w-full">
                  <div className="w-14 h-14 rounded-full bg-[#051C33] flex items-center justify-center">
                    <img src={uploadIcon} alt="Upload" className="w-5 h-5" />
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <p className="text-[#FEFCEB] text-sm font-semibold">Upload file</p>
                    <p className="text-app-text-secondary text-xs font-normal">
                      Drag & drop or <span className="text-[#429ABF] underline">choose file</span> to upload
                    </p>
                  </div>
                </div>
              )}
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(e.target.files[0])}
              />
            </div>
          </div>

        </div>

        {/* Buttons */}
        <div className="w-full max-w-[480px] flex flex-col gap-[12px] items-center justify-center shrink-0 mb-4">
          {/* Continue Button */}
          <button
            onClick={handleNextClick}
            disabled={!tournamentName.trim() || !startDate || !endDate}
            className={`w-full h-[44px] px-[16px] py-[8px] rounded-full text-sm font-medium transition-all ${
              !tournamentName.trim() || !startDate || !endDate
                ? "bg-[#002F41] text-[#021529] cursor-not-allowed"
                : "bg-[#00E7FF] text-[#021529] hover:bg-[#00cce6]"
            }`}
          >
            Continue
          </button>

          {/* Cancel Link */}
          <button
            onClick={() => navigate(DASHBOARD_STACK.HOME)}
            className="h-[32px] px-[8px] py-0 text-app-text-secondary text-sm font-medium rounded-lg hover:text-[#FEFCEB] transition-colors"
          >
            Cancel competition creation
          </button>

        </div>
      </div>
    </div>
  );
};

export default CompetitionsRefactored;
